/**
 * Build <img srcset> strings for our /api/user-photos/:token endpoint, which
 * supports ?size=thumb|medium|large|xlarge|full (see server/routes/userPhotosRoutes.js).
 *
 * Only same-origin user-photo URLs get srcset — external URLs (Wikimedia
 * hotlinks, Google Places proxy, Ticketmaster event images) don't honor the
 * size query, so callers fall back to a plain src. The path matching here
 * only looks for '/api/user-photos/' and never parses the token segment.
 */

import { sizedPhotoUrl, tiqetsResize, isCdnPhotoUrl } from './imageSource';

const USER_PHOTO_PATH = '/api/user-photos/';
const TIQETS_HOST = 'aws-tiqets-cdn.imgix.net';

// Same-origin API URLs resize via ?size=; R2/CDN URLs via the /<size> path
// segment. sizedPhotoUrl handles both shapes.
const canResize = (url) =>
  typeof url === 'string' && (url.includes(USER_PHOTO_PATH) || isCdnPhotoUrl(url));

/**
 * Returns a srcset string with thumb/medium/large/xlarge variants for resizable URLs,
 * or null for URLs that don't support resizing (caller should just use src).
 *
 * Width descriptors must match the actual served pixel width per
 * server/services/thumbnailService.js#SIZES — the browser uses these to pick
 * the right candidate, so stale numbers cause silently-wrong choices.
 *
 * Tiqets imgix covers (square crops) get their own srcset: imgix renders any
 * edge on demand, and the width descriptor equals the edge. The 1284 cap is
 * the largest Tiqets serves.
 */
export function buildPhotoSrcSet(url) {
  if (typeof url === 'string' && url.includes(TIQETS_HOST)) {
    return `${tiqetsResize(url, 128)} 128w, ${tiqetsResize(url, 250)} 250w, ${tiqetsResize(url, 500)} 500w, ${tiqetsResize(url, 1284)} 1284w`;
  }
  if (!canResize(url)) return null;
  return `${sizedPhotoUrl(url, 'thumb')} 200w, ${sizedPhotoUrl(url, 'medium')} 600w, ${sizedPhotoUrl(url, 'large')} 1200w, ${sizedPhotoUrl(url, 'xlarge')} 2400w`;
}

/**
 * Sane default `sizes` for typical feed/grid layouts (single-column mobile, multi-column desktop).
 * Override per component when layout differs significantly.
 */
export const DEFAULT_SIZES = '(max-width: 640px) 300px, (max-width: 1024px) 600px, 600px';

/**
 * Append ?size=<size> to a same-origin user-photo URL.
 * Returns the URL unchanged for external sources that don't honor the param.
 */
export function withPhotoSize(url, size) {
  return sizedPhotoUrl(url, size);
}
